(ns frontend.view.grid.common.utils
  (:require
   [common.import-permissions :as import-permissions]
   [common.localization :as loc]
   [common.permissions.entity-permissions :as entity-permissions]
   [common.util :as cu]
   [frontend.actions.account :as account-actions]
   [frontend.state.current-session :as session]
   [frontend.view.grid.common.events :as common-events]
   [ui.components.button.view :as button]
   [ui.components.toggle.view :as toggle]
   [ui.dom-globals :as dg]
   [ui.editors.entity-creator.view :as creator-editor]))

(defn export-with-persons-button [{:keys [account-teams entity-type button-ord grid-state column-keys-fn]}]
  (let [export-access? (import-permissions/export-access? @session/tenant account-teams @session/role)]
    (when (seq (:sorted-grid-entities @grid-state))
      {:toolbar-button?  true
       :dropdown-action? true
       :hidden?          (fn []
                           (or (not (import-permissions/allow-export? @session/tenant @session/role))
                               (session/is-mobile?)))
       :disabled?        (not (import-permissions/export-access? @session/tenant account-teams @session/role))
       :tooltip-text     (when-not export-access?
                           (loc/tr :export-not-allowed))
       :ord              button-ord
       :primary-button?  false
       :on-select        (fn [_]
                           (common-events/export-with-contacts-data {:entity-type        entity-type
                                                                     :grid-entities-atom grid-state
                                                                     :column-keys        (column-keys-fn)}))
       :label            (loc/tr :action/export-with-persons)})))

(defn hide-empty-kanban-groups-toggle [_container-id kanban-state]
  (let [hide-empty-groups? (:hide-all-empty-groups? @kanban-state)]
    [button/square-secondary-open
     {:tooltip  (if hide-empty-groups?
                  (loc/tr :show-empty-groups)
                  (loc/tr :hide-empty-groups))
      :on-click   #(common-events/toggle-hide-empty-groups! kanban-state)}
     (if hide-empty-groups?
       [:i.fal.fa-eye]
       [:i.fal.fa-eye-slash])]))

(defn export-button [{:keys [entity-type account-teams button-ord grid-state column-keys-fn]}]
  (let [export-access? (import-permissions/export-access? @session/tenant account-teams @session/role)]
    (when (seq (:sorted-grid-entities @grid-state))
      {:toolbar-button?  true
       :dropdown-action? true
       :disabled?        (not export-access?)
       :tooltip-text     (when-not export-access?
                           (loc/tr :export-not-allowed))
       :hidden?          (fn []
                           (or (not export-access?)
                               (session/is-mobile?)))
       :ord              button-ord
       :primary-button?  false
       :on-select        (fn [_]
                           (common-events/export-data {:entity-type         entity-type
                                                       :resolve-column-keys column-keys-fn
                                                       :grid-entities-atom  grid-state}))
       :label            (loc/tr :action/export)})))

(defn- preferred-landing-page-toggle-switch
  "Rendered as a component, not as pre-built hiccup, so `checked?` is read on
  every render rather than once when the menu item is constructed.

  The legacy grids build their menu inside a view function, so the old shape
  happened to re-render with the session. The new listing builds its toolbar
  actions once in an `r/with-let` and stores them in app-db, which froze
  `checked?` at mount: the preference saved fine and the toggle never moved.
  Reading the session inside a component is the sanctioned reactive path (see
  the re-frame notes in CLAUDE.md); reading it while *building* stored data is
  what went wrong."
  [preferred-landing-page]
  (let [account-preferences (:account/preferences @session/account)
        checked? (= preferred-landing-page (get account-preferences :preferred-landing-page))]
    [toggle/toggle
     {:checked?  checked?
      :on-change (fn [checked?]
                   (account-actions/update-preferences
                    {:preferred-landing-page (when checked? preferred-landing-page)}))}]))

(defn preferred-landing-page-toggle
  "The dropdown renders a custom item as the Radix `asChild` child, and Radix
  merges the menu item classes into that child. The child must be a DOM
  element. A Reagent component ignores the merged props, and the row then
  loses its font size, padding and hover style. Only the switch is a
  component.

  The legacy toolbar gives each item an empty icon, which moves the labels
  to the right. The label inset matches that. Set `:inset?` to false for a
  menu without the empty icon, for example the new listing. The stored
  preference does not include `:inset?`."
  [{:keys [_view-id _pipeline-id inset?]
    :or   {inset? true}
    :as   args}]
  (let [preferred-landing-page (dissoc args :inset?)]
    {:custom-item? true
     :dropdown-action? true
     :ord 99
     :show-tooltip? true
     :tooltip-text (loc/tr :preferred-landing-page-info)
     :value        [:div
                    {:class    "relative flex items-center justify-between gap-2"
                     :on-click dg/prevent-and-stop}
                    [:div
                     {:class (if inset?
                               "flex items-center ml-2"
                               "flex items-center")}
                     [:span (loc/tr :preferred-landing-page)]]
                    [:div
                     {:on-click dg/stop-propagation}
                     [preferred-landing-page-toggle-switch preferred-landing-page]]]}))

(def opportunity-config-info
  {:entity.type/oppo
   {:flow-fn session/deal-creation-flow?
    :title-loc-key :entry-criteria/no-target-type-label-oppo}
   :entity.type/fundraising-opportunity
   {:flow-fn session/froppo-creation-flow?
    :title-loc-key :entry-criteria/no-target-type-label-fundraising-opportunity}
   :entity.type/sales-opportunity
   {:flow-fn session/sales-oppo-creation-flow?
    :title-loc-key :entry-criteria/no-target-type-label-sales-opportunity}
   :entity.type/exit-opportunity
   {:flow-fn session/exit-oppo-creation-flow?
    :title-loc-key :entry-criteria/no-target-type-label-exit-opportunity}})

(defn investor-target-profile-pipeline?
  "True for pipelines whose creator-config target-type is a legal-entity flavor
   (company/person) rather than the pipeline's `:pipeline/target-asset-types`.
   Froppo and exit-oppo pipelines are always investor-target-profile;
   sales-oppo is investor-target-profile when investor-only. Mirrors the inlined
   logic in settings2/{fundraising,sales,exit}-pipelines."
  [{:keys [entity-type pipeline]}]
  (case entity-type
    :entity.type/fundraising-opportunity true
    :entity.type/exit-opportunity true
    :entity.type/sales-opportunity (= (set (:pipeline/target-asset-types pipeline))
                                      #{:entity.type/investor})
    false))

(defn resolve-config-target-types
  [{:keys [pipeline] :as args}]
  (if (investor-target-profile-pipeline? args)
    [:entity.type/company :entity.type/person]
    (:pipeline/target-asset-types pipeline)))

(defn target-config-candidates
  [{:keys [entity-type pipeline] :as args}]
  (cu/concatv
   (for [target-type (resolve-config-target-types args)]
     {:key (str "creator-config-target-" target-type)
      :label (loc/tr target-type)
      :on-click (fn [_]
                  (creator-editor/open-config!
                   {:entity-type entity-type
                    :pipeline pipeline
                    :target-type target-type}))})
   (when (:pipeline/allow-without-target? pipeline)
     [{:key "creator-config-target-nil"
       :label (loc/tr :unknown)
       :on-click (fn [_]
                   (creator-editor/open-config!
                    {:entity-type entity-type
                     :pipeline pipeline
                     :target-type nil}))}])))

(defn- resolve-creator-config-decision
  [{:keys [entity-type pipeline]}]
  (let [{:keys [flow-fn title-loc-key]} (opportunity-config-info entity-type)
        opportunity? (some? flow-fn)
        opportunity-ok? (and opportunity? (flow-fn))
        single-entity-ok? (and (not opportunity?)
                               (session/custom-create-config?)
                               (contains? entity-permissions/configurable-creator-entities entity-type))
        candidates (when opportunity-ok?
                     (target-config-candidates {:entity-type entity-type
                                                :pipeline pipeline}))]
    {:opportunity-ok?   opportunity-ok?
     :single-entity-ok? single-entity-ok?
     :candidates        candidates
     :title-loc-key     title-loc-key}))

(defn creator-config-item
  [{:keys [entity-type ord] :as args}]
  (let [{:keys [opportunity-ok? single-entity-ok? candidates title-loc-key]}
        (resolve-creator-config-decision args)
        base {:ord ord
              :toolbar-button? false
              :dropdown-action? true
              :add-ellipsis? true
              :label (loc/tr :creator-config-item-label)}]
    (cond
      (not (session/is-admin?))
      nil

      (and opportunity-ok? (= 1 (count candidates)))
      (assoc base :on-select (:on-click (first candidates)))

      (and opportunity-ok? (< 1 (count candidates)))
      (assoc base
             :sub-menu-side :left
             :add-ellipsis? false
             :items (into [{:key "creator-config-target-title"
                            :section-label? true
                            :label (loc/tr title-loc-key)}]
                          candidates))

      single-entity-ok?
      (assoc base :on-select
             (fn [_]
               (creator-editor/open-config!
                {:entity-type entity-type}))))))

(defn creator-config-toolbar-action
  "Same admin-only creator-config action as `creator-config-item`, shaped for the
   generic listing toolbar contract (:id/:label/:visible?/:enabled?/:on-click/:items)
   used by frontend.ux-overhaul.listing.pipeline-entity instead of the legacy
   :toolbar-button?/:dropdown-action?/:on-select shape."
  [{:keys [entity-type] :as args}]
  (when (session/is-admin?)
    (let [{:keys [opportunity-ok? single-entity-ok? candidates]}
          (resolve-creator-config-decision args)
          base {:id       :creator-config
                :label    (loc/tr :creator-config-item-label)
                :visible? true
                :enabled? true}]
      (cond
        (and opportunity-ok? (= 1 (count candidates)))
        (assoc base :on-click (:on-click (first candidates)))

        (and opportunity-ok? (< 1 (count candidates)))
        (assoc base
               :items (mapv (fn [{:keys [key label on-click]}]
                              {:id       (keyword key)
                               :label    label
                               :visible? true
                               :enabled? true
                               :on-click on-click})
                            candidates))

        single-entity-ok?
        (assoc base :on-click
               (fn [_]
                 (creator-editor/open-config!
                  {:entity-type entity-type})))))))
